import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { DashboardPageModel } from '../pageModels/DashboardPageModel';
import { Card, Row, Col, Statistic, Spin, DatePicker, ConfigProvider } from 'antd';
import { ShoppingCartOutlined, WalletOutlined, BankOutlined, CheckCircleOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import zhCN from 'antd/locale/zh_CN';
import '../styles/Dashboard.css';

// 设置dayjs为中文
dayjs.locale('zh-cn');

const pageModel = new DashboardPageModel();

const Dashboard: React.FC = observer(() => {
    useEffect(() => {
        pageModel.fetchOverview();
        pageModel.fetchHistory();
    }, []);

    // 处理月份变化
    const handleMonthChange = (date: any) => {
        if (date) {
            const year = date.year();
            const month = String(date.month() + 1).padStart(2, '0');
            pageModel.setSelectedMonth(`${year}-${month}`);
        }
    };

    // 图表配置
    const getChartOption = () => {
        const dates = pageModel.historyData.map(item => 
            dayjs(item.monthTime).format('YYYY-MM')
        );

        return {
            title: {
                text: '历史数据趋势',
                left: 'center'
            },
            tooltip: {
                trigger: 'axis',
                formatter: function(params: any) {
                    let result = params[0].axisValue + '<br/>';
                    params.forEach((item: any) => {
                        result += item.marker + ' ' + item.seriesName + ': ' + 
                                 new Intl.NumberFormat('zh-CN', {
                                     style: 'currency',
                                     currency: 'CNY',
                                     minimumFractionDigits: 2
                                 }).format(item.value) + '<br/>';
                    });
                    return result;
                }
            },
            legend: {
                data: ['支付订单金额', '企业充值金额', '企业提现金额', '已完成订单金额'],
                bottom: 0
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '60px',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dates
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (value: number) => {
                        return (value / 10000) + 'w';
                    }
                }
            },
            series: [
                {
                    name: '支付订单金额',
                    type: 'line',
                    smooth: true,
                    data: pageModel.historyData.map(item => item.monthPaidOrderAmount),
                    itemStyle: { color: '#3f8600' }
                },
                {
                    name: '企业充值金额',
                    type: 'line',
                    smooth: true,
                    data: pageModel.historyData.map(item => item.monthChargeAmount),
                    itemStyle: { color: '#1890ff' }
                },
                {
                    name: '企业提现金额',
                    type: 'line',
                    smooth: true,
                    data: pageModel.historyData.map(item => item.monthWithdrawAmount),
                    itemStyle: { color: '#cf1322' }
                },
                {
                    name: '已完成订单金额',
                    type: 'line',
                    smooth: true,
                    data: pageModel.historyData.map(item => item.monthFinishedOrderAmount),
                    itemStyle: { color: '#52c41a' }
                }
            ]
        };
    };

    if (pageModel.loading) {
        return (
            <div className="loading-container">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <ConfigProvider locale={zhCN}>
            <div className="dashboard-container">
                <div className="dashboard-header">
                    <h1 className="dashboard-title">数据概览</h1>
                    <DatePicker
                        defaultValue={pageModel.selectedMonth ? dayjs(pageModel.selectedMonth + '-01') : undefined}
                        onChange={handleMonthChange}
                        picker="month"
                        placeholder="选择月份"
                        style={{ width: 150 }}
                        format="YYYY-MM"
                    />
                </div>
                <Row gutter={[24, 24]}>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="dashboard-card">
                            <div className="dashboard-icon">
                                <ShoppingCartOutlined />
                            </div>
                            <Statistic
                                title="本月支付订单金额"
                                value={pageModel.salesOverview?.monthPaidOrderAmount}
                                precision={2}
                                prefix="¥"
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="dashboard-card">
                            <div className="dashboard-icon">
                                <WalletOutlined />
                            </div>
                            <Statistic
                                title="本月企业充值金额"
                                value={pageModel.salesOverview?.monthChargeAmount}
                                precision={2}
                                prefix="¥"
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="dashboard-card">
                            <div className="dashboard-icon">
                                <BankOutlined />
                            </div>
                            <Statistic
                                title="本月企业提现金额"
                                value={pageModel.salesOverview?.monthWithdrawAmount}
                                precision={2}
                                prefix="¥"
                                valueStyle={{ color: '#cf1322' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="dashboard-card">
                            <div className="dashboard-icon">
                                <CheckCircleOutlined />
                            </div>
                            <Statistic
                                title="本月已完成订单金额"
                                value={pageModel.salesOverview?.monthFinishedOrderAmount}
                                precision={2}
                                prefix="¥"
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                </Row>
                <div className="dashboard-chart">
                    <ReactECharts 
                        option={getChartOption()} 
                        style={{ height: '400px', marginTop: '24px' }}
                        opts={{ renderer: 'svg' }}
                    />
                </div>
            </div>
        </ConfigProvider>
    );
});

export default Dashboard; 